/**
 * The theme on the page (docs/DARK-MODE.md). Settings → Theme is `system` (match the phone),
 * `light` or `dark`. `light` and `dark` set `data-theme` on <html>, which tokens.css reads
 * over `prefers-color-scheme`; `system` removes it, so the phone decides.
 *
 * index.html's inline script does the same before the first paint (no flash of the wrong
 * paper), from the same stored blob; this keeps it in step afterwards: a change in
 * Settings, or a settings blob carried in on a first launch from the home screen.
 *
 * The browser's own chrome follows the page: index.html has one `theme-color` meta per
 * scheme (by `media`), and when the player overrides the phone both take the chosen paper.
 */
import { settings, type Theme } from './settings.ts';

/** Each printing's paper (tokens.css `--paper`; index.html's theme-color metas). */
export const THEME_COLOURS = { light: '#f4f0e6', dark: '#1a1814' } as const;

type Scheme = keyof typeof THEME_COLOURS;
const isScheme = (value: string | undefined): value is Scheme => value === 'light' || value === 'dark';

/** The browser chrome's colour for each scheme's meta: its own paper, or one chosen. */
function chrome(theme: Theme, doc: Document): void {
	for (const meta of doc.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"][data-scheme]')) {
		const scheme = meta.dataset.scheme;
		if (!isScheme(scheme)) continue;
		meta.content = THEME_COLOURS[theme === 'system' ? scheme : theme];
	}
}

/** Put `theme` on the page: <html data-theme>, and the browser chrome's colour. */
export function applyTheme(theme: Theme, doc: Document = document): void {
	const root = doc.documentElement;
	if (theme === 'system') delete root.dataset.theme;
	else root.dataset.theme = theme;
	chrome(theme, doc);
}

/** Apply the stored theme now and whenever it changes. Returns the unsubscribe. */
export function initTheme(): () => void {
	const store = settings();
	let current = store.get().theme;
	applyTheme(current);
	return store.subscribe(() => {
		const next = store.get().theme;
		if (next === current) return;
		current = next;
		applyTheme(next);
	});
}
